/**
 * ErrorBoundary — catches uncaught React render errors inside a subtree.
 *
 * Degrades to an inline "Something went wrong" card instead of unmounting
 * the whole app. Wrap any panel or page section that does data-driven
 * rendering so a bad API shape can't blank the entire UI.
 *
 * Usage:
 *   <ErrorBoundary>
 *     <MyDataPanel />
 *   </ErrorBoundary>
 *
 * Custom fallback:
 *   <ErrorBoundary fallback={<p>Oops</p>}>
 *     <MyDataPanel />
 *   </ErrorBoundary>
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';

type Props = {
  children: ReactNode;
  /** Optional custom fallback. Defaults to the inline error card. */
  fallback?: ReactNode;
};

type State = {
  hasError: boolean;
  message: string | null;
};

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, message: null };
  }

  static getDerivedStateFromError(error: unknown): State {
    const message =
      error instanceof Error ? error.message : String(error);
    return { hasError: true, message };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // Log to the browser console for dev debugging. A production logger
    // (Sentry, CloudWatch RUM) can be wired here when available.
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false, message: null });
  };

  override render() {
    if (this.state.hasError) {
      if (this.props.fallback !== undefined) {
        return this.props.fallback;
      }
      return (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900/50 dark:bg-red-950/20">
          <p className="text-sm font-medium text-red-700 dark:text-red-400">
            Something went wrong loading this section.
          </p>
          {this.state.message && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-500 font-mono break-all">
              {this.state.message}
            </p>
          )}
          <button
            type="button"
            onClick={this.handleReset}
            className="mt-2 text-xs underline text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
          >
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
